<!-- Button -->
<button
  [pcMenuTriggerFor]="userActions"
  class="flex items-center gap-2 px-2 py-1 rounded-xl cursor-pointer transition-all duration-200 hover:bg-neutral-100/60 dark:hover:bg-white/5 focus:outline-none group"
>
  <div class="hidden sm:flex flex-col items-start leading-tight min-w-0">
    <span class="text-pc-body font-semibold text-[color:var(--text-title)] whitespace-nowrap">
      {{ fullName }}
    </span>
    <span class="text-pc-label text-[color:var(--text-subtitle)] whitespace-nowrap">
      {{ user.email }}
    </span>
  </div>
  <div class="relative shrink-0">
    @if (showAvatar && user.avatar) {
      <img
        class="w-7 h-7 rounded-full object-cover shadow-sm transition-all duration-200 group-hover:scale-105 ring-2 ring-white/20"
        [src]="user.avatar"
      />
    } @else {
      <span
        class="flex items-center justify-center w-7 h-7 rounded-full text-white text-pc-eyebrow font-bold uppercase shadow-sm transition-all duration-200 group-hover:scale-105 ring-2 ring-white/20"
        style="background-color: var(--main-button-bg)"
      >
        {{ initials }}
      </span>
    }
  </div>
</button>

<primecontrol-menu [xPosition]="'before'" #userActions>
  <!-- User info header -->
  <div
    class="px-3 pt-3 pb-2.5 border-b border-neutral-100 dark:border-neutral-800"
    (click)="$event.stopPropagation()"
  >
    <div class="flex items-center gap-2.5">
      @if (showAvatar && user.avatar) {
        <img
          class="w-9 h-9 rounded-full object-cover shadow-sm ring-2 ring-white/15"
          [src]="user.avatar"
        />
      } @else {
        <span
          class="flex items-center justify-center w-9 h-9 rounded-full text-white text-pc-eyebrow font-bold uppercase shrink-0 shadow-sm ring-2 ring-white/15"
          style="background-color: var(--main-button-bg)"
        >
          {{ initials }}
        </span>
      }
      <div class="min-w-0 flex-1">
        <p class="text-pc-title-sm font-semibold text-[color:var(--text-title)] truncate">
          {{ fullName }}
        </p>
        <p class="text-pc-secondary font-medium text-[color:var(--text-subtitle)] truncate">
          {{ user.email }}
        </p>
      </div>
    </div>
  </div>

  <!-- Actions -->
  <div class="p-1.5">
    <button class="user-menu-item" (click)="selectorInstance()">
      <primecontrol-svg-icon
        class="icon-size-4 shrink-0 text-[color:var(--text-subtitle)]"
        [svgIcon]="'heroicons_outline:template'"
      ></primecontrol-svg-icon>
      <span>{{ 'INSTANCES' | translate }}</span>
    </button>
    <button class="user-menu-item" (click)="goToOrganizations()">
      <primecontrol-svg-icon
        class="icon-size-4 shrink-0 text-[color:var(--text-subtitle)]"
        [svgIcon]="'heroicons_outline:office-building'"
      ></primecontrol-svg-icon>
      <span>{{ 'ORGANIZATIONS' | translate }}</span>
    </button>
    <button class="user-menu-item" (click)="goToPreferences()">
      <primecontrol-svg-icon
        class="icon-size-4 shrink-0 text-[color:var(--text-subtitle)]"
        [svgIcon]="'heroicons_outline:cog'"
      ></primecontrol-svg-icon>
      <span>{{ 'preferences' | translate }}</span>
    </button>
    <button class="user-menu-item" (click)="goToSecurity()">
      <primecontrol-svg-icon
        class="icon-size-4 shrink-0 text-[color:var(--text-subtitle)]"
        [svgIcon]="'heroicons_outline:shield-check'"
      ></primecontrol-svg-icon>
      <span>{{ 'SECURITY' | translate }}</span>
    </button>
    <div class="hidden md:block">
      <pc-fullscreen></pc-fullscreen>
    </div>
  </div>

  <!-- Theme selector -->
  <div class="px-3 pb-2.5 pt-1" (click)="$event.stopPropagation()">
    <p
      class="text-pc-eyebrow font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest px-0.5 mb-1.5"
    >
      {{ 'THEME' | translate }}
    </p>
    <div
      class="flex gap-1.5 p-1 rounded-xl border border-neutral-100 dark:border-neutral-700/60 bg-neutral-50/70 dark:bg-neutral-800/50"
    >
      <button
        (click)="setScheme('auto')"
        class="flex-1 flex flex-col items-center gap-1 py-1.5 px-1 rounded-lg text-pc-secondary font-medium transition-all duration-150 cursor-pointer"
        [ngClass]="
          baseMode() === 'system'
            ? 'bg-white dark:bg-neutral-700 shadow-sm ring-1 ring-neutral-200 dark:ring-neutral-600 text-[color:var(--text-title)]'
            : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700/40'
        "
      >
        <primecontrol-svg-icon
          [svgIcon]="'heroicons_outline:desktop-computer'"
          class="icon-size-4"
        ></primecontrol-svg-icon>
        <span>{{ 'SYSTEM' | translate }}</span>
      </button>
      <button
        (click)="setScheme('light')"
        class="flex-1 flex flex-col items-center gap-1 py-1.5 px-1 rounded-lg text-pc-secondary font-medium transition-all duration-150 cursor-pointer"
        [ngClass]="
          baseMode() === 'light'
            ? 'bg-white dark:bg-neutral-700 shadow-sm ring-1 ring-neutral-200 dark:ring-neutral-600 text-[color:var(--text-title)]'
            : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700/40'
        "
      >
        <primecontrol-svg-icon
          [svgIcon]="'heroicons_outline:sun'"
          class="icon-size-4"
        ></primecontrol-svg-icon>
        <span>{{ 'LIGHT_MODE' | translate }}</span>
      </button>
      <button
        (click)="setScheme('dark')"
        class="flex-1 flex flex-col items-center gap-1 py-1.5 px-1 rounded-lg text-pc-secondary font-medium transition-all duration-150 cursor-pointer"
        [ngClass]="
          baseMode() === 'dark'
            ? 'bg-white dark:bg-neutral-700 shadow-sm ring-1 ring-neutral-200 dark:ring-neutral-600 text-[color:var(--text-title)]'
            : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700/40'
        "
      >
        <primecontrol-svg-icon
          [svgIcon]="'heroicons_outline:moon'"
          class="icon-size-4"
        ></primecontrol-svg-icon>
        <span>{{ 'DARK_MODE' | translate }}</span>
      </button>
    </div>
  </div>

  <!-- Sign out -->
  <div class="border-t border-neutral-100 dark:border-neutral-800 p-1.5">
    <button class="user-menu-item danger" (click)="signOut()">
      <primecontrol-svg-icon
        class="icon-size-4 shrink-0"
        [svgIcon]="'heroicons_outline:logout'"
      ></primecontrol-svg-icon>
      <span>{{ 'CLOSE_SESSION' | translate }}</span>
    </button>
  </div>
</primecontrol-menu>

<primecontrol-menu class="user-status-menu" #userStatus>
  <button pc-menu-item (click)="updateUserStatus('online')">
    <span class="w-4 h-4 mr-3 bg-green-500 rounded-full"></span>
    <span>{{ 'ONLINE' | translate }}</span>
  </button>
  <button pc-menu-item (click)="updateUserStatus('away')">
    <span class="w-4 h-4 mr-3 rounded-full bg-amber-500"></span>
    <span>Ausente</span>
  </button>
  <button pc-menu-item (click)="updateUserStatus('busy')">
    <span class="w-4 h-4 mr-3 bg-red-500 rounded-full"></span>
    <span>{{ 'BUSY' | translate }}</span>
  </button>
  <button pc-menu-item (click)="updateUserStatus('not-visible')">
    <span class="w-4 h-4 mr-3 bg-gray-400 rounded-full"></span>
    <span>{{ 'OFFLINE' | translate }}</span>
  </button>
</primecontrol-menu>
